'use client';

import { Accordion } from '@ironhack/design-system2/components/accordion';
import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { backgroundToTokens } from '@ironhack/design-system2/utils';
import { range, uniq } from 'ramda';
import { type ReactElement, useCallback, useState } from 'react';

import { Button } from '@/app/components/client/button';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoModuleFormat,
} from '@/lib/datocms/types';
import { ExpandCollapseCard } from './expand-collapse-card';
import { ExpandCollapseFilter } from './expand-collapse-filter';
import { SeoFaqPageStructuredData } from './seo-faq-page-structured-data';

export type ExpandCollapse = DatoBlock<{
  allOpen?: boolean;
  button?: DatoButtonBlock | null;
  cards: ExpandCollapseCard[];
  faqPageStructuredData?: boolean;
  filter: boolean;
  filterPlaceholder?: string;
  headingProps?: TextProps | null;
  moduleFormat: [DatoModuleFormat];
  subtitle?: string;
  title?: string;
}>;

export type Props = { data: ExpandCollapse } & BoxProps;

export const ExpandCollapse = forwardRef((props: Props, ref): ReactElement => {
  const {
    data: {
      allOpen = false,
      button,
      cards,
      faqPageStructuredData,
      filter,
      filterPlaceholder,
      headingProps,
      moduleFormat: [moduleFormat],
      subtitle,
      title,
    },
    title: navTitle,
    ...boxProps
  } = props;

  const filterValues = uniq(cards.flatMap((card) => card.filterValues || []));
  const [filterValue, setFilterValue] = useState<string>('');
  const [filteredCards, setFilteredCards] = useState(cards);

  const handleFilterChange = useCallback(
    (newFilterValue: string) => {
      const newFilteredCards = newFilterValue
        ? cards.filter(({ filterValues: cardFilterValues }) =>
            cardFilterValues?.includes(newFilterValue)
          )
        : cards;
      setFilteredCards(newFilteredCards);
      setFilterValue(newFilterValue);
    },
    [cards]
  );

  const { params } = usePageContext();

  const desktopAlign = filter ? null : 'center';
  const { color } = backgroundToTokens(moduleFormat.background);

  return (
    <>
      <ModuleWrapper
        innerProps={{
          align: ['start', null, desktopAlign],
          direction: 'column',
          gap: 3,
          maxW: '720px',
          mx: 'auto',
          px: [2, null, 0],
          textAlign: ['start', null, desktopAlign],
        }}
        moduleFormat={moduleFormat}
        outerProps={boxProps}
        ref={ref}
        title={navTitle}
      >
        {title && (
          <Text as="h2" textStyle="4xl" {...headingProps}>
            {title}
          </Text>
        )}
        {subtitle && <Text textStyle="l">{subtitle}</Text>}

        {filter && (
          <ExpandCollapseFilter
            color={color}
            onChangeValue={handleFilterChange}
            placeholder={filterPlaceholder}
            value={filterValue}
            values={filterValues}
          />
        )}

        <Accordion
          allowMultiple
          as={Flex}
          defaultIndex={allOpen ? range(0, filteredCards.length) : [0]}
          direction="column"
          gap={3}
        >
          {filteredCards.map((card, index) => (
            <ExpandCollapseCard
              card={card}
              key={`expand-collapse-card-${index}`}
              {...(faqPageStructuredData && { headingProps: { as: 'h3' } })}
            />
          ))}
        </Accordion>

        {button && <Button alignSelf="center" {...button} />}
      </ModuleWrapper>

      {faqPageStructuredData && (
        <SeoFaqPageStructuredData cards={cards} params={params} />
      )}
    </>
  );
});
